import React from 'react';
import PropTypes from 'prop-types';

import Can from 'components/Can';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CloseIcon from '@mui/icons-material/Close';
import IconButton from '@mui/material/IconButton';
import InfoIcon from '@mui/icons-material/Info';

import {
  ModalRoot,
  ModalContainer,
  ModalBtn,
  ModalHeader,
  ModalHeaderPadding,
  ModalFooter,
  ModalHeaderTitle,
  ModalBody,
  ModalSm,
  ModalMd,
  ModalXl,
  ModalLg,
} from './Modal.styled';
import Tooltip from '@mui/material/Tooltip';
import { SlideInDown } from '../../Modals/Guiding/SlideInDown';

const renderCloseButton = (type, onClick) => {
  if (type === 'icon') {
    return (
      <IconButton data-cy="close-button" color="secondary" aria-label="close" onClick={onClick}>
        <CloseIcon />
      </IconButton>
    );
  }

  return (
    <Button data-cy="close-button" color="primary" onClick={onClick} sx={{ fontWeight: 'bold' }}>
      Close
    </Button>
  );
};

const InfoTooltip = ({ title, className }) => {
  return (
    <Tooltip title={title} className={className}>
      <InfoIcon color="primary" />
    </Tooltip>
  );
};

InfoTooltip.propTypes = {
  title: PropTypes.string,
  className: PropTypes.string,
};

const RenderEnterButton = ({
  sx,
  enterButtonLabel,
  loading,
  disabled,
  onClick,
  buttonsVariant,
  ...props
}) => (
  <Button
    size="large"
    color="primary"
    variant={buttonsVariant}
    onClick={onClick}
    disabled={loading || disabled}
    sx={sx}
    {...props}
  >
    {enterButtonLabel}
  </Button>
);

RenderEnterButton.propTypes = {
  sx: PropTypes.oneOfType([PropTypes.object, PropTypes.func, PropTypes.array]),
  enterButtonLabel: PropTypes.string,
  loading: PropTypes.bool,
  disabled: PropTypes.bool,
  onClick: PropTypes.func,
  buttonsVariant: PropTypes.string,
};

renderCloseButton.propTypes = {
  onClick: PropTypes.func.isRequired,
  type: PropTypes.oneOf(['label', 'icon']).isRequired,
};

const ModalComponent = ({
  size,
  maxWidth,
  title,
  children,
  open,
  handleClick,
  enterButtonProps,
  enableCloseButton,
  closeButtonType,
  hasEnterButton,
  loading,
  id,
  hasCancelButton,
  onEnter,
  onCancel,
  enterButtonLabel,
  disabled,
  disabledCancelButton,
  cancelButtonLabel,
  hasHeader,
  hasFooter,
  privileges,
  containerCustomClass,
  containerClassName,
  bodyClassName = '',
  footerClassName = '',
  buttonsVariant,
  enterVariant,
  enterTitleToolTip = null,
  cancelTitleToolTip = null,
  ...props
}) => {
  // Modal size mapping
  const sizeComponent =
    {
      sm: ModalSm,
      md: ModalMd,
      lg: ModalLg,
      xl: ModalXl,
    }[size] || ModalMd;

  return (
    <ModalRoot
      open={open}
      id={id || 'modal'}
      onClose={handleClick}
      sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '100%' }}
      aria-labelledby={`modal-${title}`}
      aria-describedby={`modal-${title}`}
      {...props}
    >
      <SlideInDown in={open}>
        <ModalContainer
          as={sizeComponent}
          sx={{ maxWidth, width: '100%' }}
          className={containerClassName}
        >
          {hasHeader && (
            <ModalHeaderPadding as={ModalHeader} className={containerCustomClass}>
              <ModalHeaderTitle component="h2" data-cy="modalTitle">
                {title}
              </ModalHeaderTitle>
              {enableCloseButton && renderCloseButton(closeButtonType, handleClick)}
            </ModalHeaderPadding>
          )}
          <ModalBody className={bodyClassName}>{children}</ModalBody>
          {hasFooter && (
            <ModalFooter className={footerClassName}>
              {hasCancelButton && (
                <Box>
                  <Button
                    data-cy="btn-cancel"
                    size="large"
                    variant={buttonsVariant}
                    color="primary"
                    sx={ModalBtn}
                    onClick={onCancel}
                    disabled={loading || disabledCancelButton}
                  >
                    {cancelButtonLabel}
                  </Button>
                  {cancelTitleToolTip && (
                    <InfoTooltip title={cancelTitleToolTip} className="modal-tooltip-size" />
                  )}
                </Box>
              )}

              {hasEnterButton && (
                <>
                  {privileges ? (
                    <Can I={privileges?.action} a={privileges?.subject}>
                      <RenderEnterButton
                        onClick={onEnter}
                        buttonsVariant={enterVariant || buttonsVariant}
                        data-cy="btn-save"
                        sx={ModalBtn}
                        disabled={loading || disabled}
                        enterButtonLabel={enterButtonLabel}
                        {...enterButtonProps}
                      />
                      {enterTitleToolTip && <InfoTooltip title={enterTitleToolTip} />}
                    </Can>
                  ) : (
                    <>
                      <RenderEnterButton
                        onClick={onEnter}
                        buttonsVariant={enterVariant || buttonsVariant}
                        data-cy="btn-save"
                        sx={ModalBtn}
                        disabled={loading || disabled}
                        enterButtonLabel={enterButtonLabel}
                        {...enterButtonProps}
                      />
                      {enterTitleToolTip && <InfoTooltip title={enterTitleToolTip} />}
                    </>
                  )}
                </>
              )}
            </ModalFooter>
          )}
        </ModalContainer>
      </SlideInDown>
    </ModalRoot>
  );
};

ModalComponent.propTypes = {
  title: PropTypes.string,
  disabled: PropTypes.bool,
  hasHeader: PropTypes.bool,
  hasFooter: PropTypes.bool,
  privileges: PropTypes.object,
  enableCloseButton: PropTypes.bool,
  enterButtonLabel: PropTypes.string,
  cancelButtonLabel: PropTypes.string,
  containerCustomClass: PropTypes.any,
  children: PropTypes.node.isRequired,
  size: PropTypes.oneOf(['sm', 'md', 'lg', 'xl']),
  closeButtonType: PropTypes.oneOf(['label', 'icon']),
  buttonsVariant: PropTypes.string,
  enterVariant: PropTypes.string,
  maxWidth: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  open: PropTypes.bool,
  handleClick: PropTypes.func,
  enterButtonProps: PropTypes.object,
  hasEnterButton: PropTypes.bool,
  loading: PropTypes.bool,
  id: PropTypes.string,
  hasCancelButton: PropTypes.bool,
  onEnter: PropTypes.func,
  onCancel: PropTypes.func,
  disabledCancelButton: PropTypes.bool,
  containerClassName: PropTypes.string,
  bodyClassName: PropTypes.string,
  footerClassName: PropTypes.string,
  enterTitleToolTip: PropTypes.string,
  cancelTitleToolTip: PropTypes.string,
};

ModalComponent.defaultProps = {
  size: 'md',
  disabled: false,
  hasHeader: true,
  hasFooter: true,
  closeButtonType: 'icon',
  enableCloseButton: true,
  enterButtonLabel: 'Done',
  cancelButtonLabel: 'Cancel',
  containerCustomClass: '',
  buttonsVariant: 'text',
};

export default ModalComponent;
